/**
 * Hook useTwoFactor
 * Il Diario di Bordo - SaaS
 *
 * Hook per gestione stato e operazioni 2FA.
 */

import { useState, useCallback } from 'react'
import api from '../services/api'

export function useTwoFactor() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Ottieni stato 2FA dell'utente
   */
  const getStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/auth/2fa/status')
      return response.data.data
    } catch (err) {
      const message = err.response?.data?.message || 'Errore nel recupero stato 2FA'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // ═══════════════════════════════════════════════════════════════════════════
  // TOTP
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Inizia setup TOTP
   * @returns {{ qrCode, manualEntryKey, tempSecret }}
   */
  const setupTOTP = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post('/auth/2fa/totp/setup')
      return response.data.data
    } catch (err) {
      const message = err.response?.data?.message || 'Errore setup TOTP'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Conferma setup TOTP
   * @param {string} code - Codice a 6 cifre
   * @param {string} tempSecret - Secret temporaneo
   * @returns {{ backupCodes }}
   */
  const verifyTOTPSetup = useCallback(async (code, tempSecret) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post('/auth/2fa/totp/verify-setup', {
        code,
        tempSecret
      })
      return response.data.data
    } catch (err) {
      const message = err.response?.data?.message || 'Codice non valido'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Disabilita TOTP
   * @param {{ password?: string, code?: string }} confirmation
   */
  const disableTOTP = useCallback(async (confirmation) => {
    setLoading(true)
    setError(null)
    try {
      await api.delete('/auth/2fa/totp', { data: confirmation })
      return true
    } catch (err) {
      const message = err.response?.data?.message || 'Errore disabilitazione TOTP'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // ═══════════════════════════════════════════════════════════════════════════
  // EMAIL OTP
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Abilita Email OTP
   * @returns {{ backupCodes }}
   */
  const enableEmailOTP = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post('/auth/2fa/email/enable')
      return response.data.data
    } catch (err) {
      const message = err.response?.data?.message || 'Errore abilitazione Email OTP'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Invia codice Email OTP
   */
  const sendEmailOTP = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await api.post('/auth/2fa/email/send')
      return true
    } catch (err) {
      const message = err.response?.data?.message || 'Errore invio codice'
      setError(message)
      // Estrai retryAfter se presente
      const retryAfter = err.response?.data?.retryAfter
      throw { message, retryAfter }
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Disabilita Email OTP
   * @param {{ password?: string }} confirmation
   */
  const disableEmailOTP = useCallback(async (confirmation = {}) => {
    setLoading(true)
    setError(null)
    try {
      await api.delete('/auth/2fa/email', { data: confirmation })
      return true
    } catch (err) {
      const message = err.response?.data?.message || 'Errore disabilitazione Email OTP'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // ═══════════════════════════════════════════════════════════════════════════
  // WEBAUTHN / PASSKEYS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Registra nuova passkey
   * @param {string} deviceName - Nome del dispositivo
   * @returns {{ passkey, backupCodes }}
   */
  const registerPasskey = useCallback(async (deviceName = 'Dispositivo') => {
    setLoading(true)
    setError(null)
    try {
      // 1. Ottieni opzioni dal server
      const optionsResponse = await api.post('/auth/2fa/webauthn/register-options', {
        deviceName
      })
      const options = optionsResponse.data.data

      // 2. Chiama WebAuthn API del browser
      // Converti base64url a ArrayBuffer
      options.challenge = base64urlToBuffer(options.challenge)
      options.user.id = base64urlToBuffer(options.user.id)
      
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(cred => ({
          ...cred,
          id: base64urlToBuffer(cred.id)
        }))
      }

      const credential = await navigator.credentials.create({ publicKey: options })

      // 3. Prepara response per il server
      const registrationResponse = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject),
          transports: credential.response.getTransports?.() || []
        }
      }

      // 4. Verifica sul server (invia campi direttamente + name)
      const verifyResponse = await api.post('/auth/2fa/webauthn/register-verify', {
        ...registrationResponse,
        name: deviceName
      })

      return verifyResponse.data.data
    } catch (err) {
      // Gestisci errori WebAuthn
      if (err.name === 'NotAllowedError') {
        setError('Operazione annullata o non permessa')
      } else if (err.name === 'InvalidStateError') {
        setError('Questa passkey è già registrata')
      } else if (err.name === 'NotSupportedError') {
        setError('WebAuthn non supportato su questo dispositivo')
      } else {
        const message = err.response?.data?.message || err.message || 'Errore registrazione passkey'
        setError(message)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Rimuovi passkey
   * @param {string} credentialId - ID della passkey
   */
  const removePasskey = useCallback(async (credentialId) => {
    setLoading(true)
    setError(null)
    try {
      await api.delete(`/auth/2fa/webauthn/${encodeURIComponent(credentialId)}`)
      return true
    } catch (err) {
      const message = err.response?.data?.message || 'Errore rimozione passkey'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Rinomina passkey
   * @param {string} credentialId - ID della passkey
   * @param {string} newName - Nuovo nome
   */
  const renamePasskey = useCallback(async (credentialId, newName) => {
    setLoading(true)
    setError(null)
    try {
      await api.put(`/auth/2fa/webauthn/${encodeURIComponent(credentialId)}/name`, {
        name: newName
      })
      return true
    } catch (err) {
      const message = err.response?.data?.message || 'Errore rinomina passkey'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // ═══════════════════════════════════════════════════════════════════════════
  // BACKUP CODES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Genera nuovi codici di backup
   * @param {string} password - Password per conferma (se codici esistono già)
   * @returns {{ codes, warning }}
   */
  const generateBackupCodes = useCallback(async (password = null) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post('/auth/2fa/backup-codes/regenerate', {
        password
      })
      return response.data.data
    } catch (err) {
      const message = err.response?.data?.message || 'Errore generazione codici'
      const code = err.response?.data?.code
      setError(message)
      throw { message, code }
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Ottieni status codici backup
   */
  const getBackupCodesStatus = useCallback(async () => {
    try {
      const response = await api.get('/auth/2fa/backup-codes/status')
      return response.data.data
    } catch (err) {
      console.error('Errore status backup codes:', err)
      return null
    }
  }, [])

  /**
   * Scarica codici di backup come PDF
   * @param {string[]} codes - Array dei codici di backup
   * @returns {Blob} PDF blob
   */
  const downloadBackupCodesPdf = useCallback(async (codes) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post('/auth/2fa/backup-codes/download-pdf', 
        { codes }, 
        { responseType: 'arraybuffer' }
      )
      
      // Crea blob e scarica
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `codici-backup-2fa-${new Date().toISOString().split('T')[0]}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      return true
    } catch (err) {
      const message = err.response?.data?.message || 'Errore download PDF'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // ═══════════════════════════════════════════════════════════════════════════
  // VERIFICA (durante login)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Verifica codice 2FA
   * @param {Object} params
   * @param {string} params.userId
   * @param {string} params.method - 'totp' | 'emailOtp' | 'backupCode'
   * @param {string} params.code
   * @param {string} params.tempToken
   * @returns {{ accessToken, refreshToken, warning? }}
   */
  const verify2FA = useCallback(async ({ userId, method, code, tempToken }) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post('/auth/2fa/verify', {
        userId,
        method,
        code,
        tempToken
      })
      return response.data.data
    } catch (err) {
      const message = err.response?.data?.message || 'Codice non valido'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Invia codice durante login (senza autenticazione)
   */
  const sendCodeDuringLogin = useCallback(async ({ userId, method, tempToken }) => {
    setLoading(true)
    setError(null)
    try {
      await api.post('/auth/2fa/send-code', {
        userId,
        method,
        tempToken
      })
      return true
    } catch (err) {
      const message = err.response?.data?.message || 'Errore invio codice'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Imposta metodo preferito
   * @param {'totp' | 'emailOtp' | 'webauthn'} method
   */
  const setPreferredMethod = useCallback(async (method) => {
    setLoading(true)
    setError(null)
    try {
      await api.put('/auth/2fa/preferred-method', { method })
      return true
    } catch (err) {
      const message = err.response?.data?.message || 'Errore impostazione metodo'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    clearError: () => setError(null),
    
    // Status
    getStatus,
    setPreferredMethod,
    
    // TOTP
    setupTOTP,
    verifyTOTPSetup,
    disableTOTP,
    
    // Email OTP
    enableEmailOTP,
    sendEmailOTP,
    disableEmailOTP,
    
    // WebAuthn
    registerPasskey,
    removePasskey,
    renamePasskey,
    isWebAuthnSupported: typeof window !== 'undefined' && 
                          window.PublicKeyCredential !== undefined,
    
    // Backup codes
    generateBackupCodes,
    getBackupCodesStatus,
    downloadBackupCodesPdf,
    
    // Verifica
    verify2FA,
    sendCodeDuringLogin
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Converte base64url a ArrayBuffer
 */
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - base64.length % 4) % 4)
  const binary = atob(base64 + padding)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Converte ArrayBuffer a base64url
 */
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64 = btoa(binary)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export default useTwoFactor
